/* Minimal Home — quiet restraint, not anemic.
   Same Aesop / Hermès Beauty DNA (centered single column, lots of whitespace,
   hairline gold accents, no chrome) but with substance below the fold so a
   visitor has reasons to scroll: a moment of stillness with the hero, a quiet
   recent-work strip, a 3-row services list, a 3-step "how it works" rhythm,
   and a single closing contact line. Every block stays one column max,
   centered, with the same hairline gold rule as a visual anchor.

   Why this isn't a clone of allure-image.com: that's the Editorial template's
   reference (richer, asymmetric, magazine-style). Minimal stays Hermès. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { api } from "@/lib/api";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";

const FALLBACK_HERO = "https://images.unsplash.com/photo-1500917293891-ef795e70e1f6?auto=format&fit=crop&w=2000&q=80";

function startingPrice(svc) {
  if (Array.isArray(svc?.tiers) && svc.tiers.length > 0) {
    const ps = svc.tiers.map((t) => Number(t?.price)).filter((n) => Number.isFinite(n) && n > 0);
    if (ps.length) return Math.min(...ps);
  }
  const flat = svc?.price_from ?? svc?.starting_price ?? svc?.price;
  const n = Number(flat);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/* Hairline gold rule used as a section anchor across the whole page. Keeps
   the restraint cue consistent without resorting to underlines or borders. */
function Hairline({ className = "" }) {
  return (
    <div
      className={`mx-auto h-px ${className}`}
      style={{ width: "2rem", background: "var(--tenant-accent, #C9A961)" }}
      aria-hidden
    />
  );
}

function Eyebrow({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-fg,#0E0D0C)]/55" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>
      {children}
    </div>
  );
}

export default function MinimalHome() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const tagline = settings?.tagline || settings?.hero_descriptor || "Portraits, considered.";
  const locale = settings?.studio_location || settings?.location || settings?.region || "Studio Portraiture";
  const portfolio = settings?.portfolio_images || [];
  const hero = settings?.hero_image_url || settings?.hero_image || portfolio[0] || FALLBACK_HERO;
  const heroPos = `${settings?.hero_focal_x ?? 50}% ${settings?.hero_focal_y ?? 50}%`;
  const [services, setServices] = useState([]);

  useEffect(() => {
    document.title = `${studioName}`;
    let meta = document.querySelector("meta[name='description']");
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", `${studioName}. ${tagline}`);
    api.get("/services").then((r) => setServices(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [studioName, tagline]);

  const featuredWork = portfolio.slice(0, 3);
  const featuredServices = services.slice(0, 4);

  return (
    <div data-testid="minimal-home" className="bg-[color:var(--tenant-bg,#FAF9F6)] text-[color:var(--tenant-fg,#0E0D0C)]">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="pt-28 sm:pt-36 pb-20 px-6" data-testid="minimal-hero">
        <div className="max-w-2xl text-center mx-auto">
          <Eyebrow>{locale}</Eyebrow>
          <Hairline className="mt-7 mb-10" />
          <h1
            className="font-light leading-[1.02] tracking-[-0.01em]"
            style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(3.5rem, 8vw, 7rem)" }}
            data-testid="minimal-home-title"
          >
            {studioName}
          </h1>
          <p
            className="mt-9 text-lg sm:text-xl italic text-[color:var(--tenant-fg,#0E0D0C)]/75 leading-relaxed max-w-lg mx-auto"
            style={{ fontFamily: "var(--tenant-serif)" }}
          >
            {tagline}
          </p>
          <div className="mt-11 inline-flex items-center gap-5 text-[11px] uppercase tracking-[0.32em]">
            <Link to={tenantHref("/portfolio")} data-testid="minimal-home-view-work"
              className="relative pb-1 hover:text-[color:var(--tenant-accent)] transition-colors"
              style={{ borderBottom: "1px solid var(--tenant-accent, #C9A961)" }}>
              View the work →
            </Link>
            <span aria-hidden className="text-[color:var(--tenant-fg,#0E0D0C)]/30">—</span>
            <Link to={tenantHref("/booking")} data-testid="minimal-home-book"
              className="relative pb-1 hover:text-[color:var(--tenant-accent)] transition-colors"
              style={{ borderBottom: "1px solid var(--tenant-accent, #C9A961)" }}>
              Book →
            </Link>
          </div>
        </div>
      </section>

      {/* ── A SINGLE PORTRAIT ───────────────────────────────────────────── */}
      <section className="pb-28 px-6" data-testid="minimal-home-photo">
        <figure className="max-w-3xl mx-auto">
          <div className="aspect-[4/5] bg-[#0E0D0C]/5 overflow-hidden">
            <img src={optimizeImageUrl(hero, { w: 1600 })}
              srcSet={optimizedSrcSet(hero, [800, 1200, 1600, 2000]) || undefined}
              sizes="(max-width: 768px) 100vw, 768px"
              alt={`${studioName} — selected plate`} className="w-full h-full object-cover"
              style={{ objectPosition: heroPos }} loading="eager" fetchpriority="high" decoding="async" />
          </div>
        </figure>
      </section>

      {/* ── A NOTE FROM THE STUDIO ──────────────────────────────────────── */}
      {settings?.about_body && (
        <section className="pb-28 px-6" data-testid="minimal-home-note">
          <div className="max-w-xl mx-auto text-center">
            <Eyebrow>From the studio</Eyebrow>
            <Hairline className="mt-7 mb-9" />
            <p className="text-xl sm:text-2xl italic leading-[1.5] text-[color:var(--tenant-fg,#0E0D0C)]/85"
               style={{ fontFamily: "var(--tenant-serif)" }}>
              {settings.about_body}
            </p>
          </div>
        </section>
      )}

      {/* ── RECENT WORK ─────────────────────────────────────────────────── */}
      {featuredWork.length > 0 && (
        <section className="pb-28 px-6" data-testid="minimal-home-recent">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <Eyebrow>Recent</Eyebrow>
              <Hairline className="mt-7" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5">
              {featuredWork.map((src, i) => (
                <Link key={i} to={tenantHref("/portfolio")} className="block group" data-testid={`minimal-recent-${i}`}>
                  <div className="aspect-[3/4] bg-[#0E0D0C]/5 overflow-hidden">
                    <img src={optimizeImageUrl(src, { w: 800 })}
                      srcSet={optimizedSrcSet(src, [400, 600, 800, 1200]) || undefined}
                      sizes="(max-width: 640px) 100vw, 320px"
                      alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700" loading="lazy" decoding="async" />
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to={tenantHref("/portfolio")} data-testid="minimal-recent-cta"
                className="text-[10px] uppercase tracking-[0.42em] pb-1 transition-colors hover:opacity-70"
                style={{ borderBottom: "1px solid var(--tenant-accent, #C9A961)", color: "#0E0D0C" }}>
                See the full portfolio →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── THE MENU (services list, restrained) ────────────────────────── */}
      {featuredServices.length > 0 && (
        <section className="pb-28 px-6" data-testid="minimal-home-menu">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <Eyebrow>The menu</Eyebrow>
              <Hairline className="mt-7" />
            </div>
            <ul className="divide-y divide-[color:var(--tenant-fg,#0E0D0C)]/12 border-y border-[color:var(--tenant-fg,#0E0D0C)]/12">
              {featuredServices.map((s) => {
                const p = startingPrice(s);
                return (
                  <li key={s.id || s.slug} data-testid={`minimal-home-svc-${s.slug || s.id}`}>
                    <Link to={tenantHref(`/booking?service=${s.slug || s.id}`)}
                      className="grid grid-cols-12 gap-4 items-baseline py-6 group hover:opacity-80 transition-opacity">
                      <span className="col-span-12 sm:col-span-7 text-xl sm:text-2xl font-light italic leading-tight"
                        style={{ fontFamily: "var(--tenant-serif)" }}>
                        {s.name}
                      </span>
                      <span className="col-span-12 sm:col-span-3 text-[13px] text-[color:var(--tenant-fg,#0E0D0C)]/60 leading-relaxed line-clamp-2">
                        {s.short_description || s.description?.slice(0, 80) || ""}
                      </span>
                      <span className="col-span-12 sm:col-span-2 text-right italic text-base sm:text-lg"
                        style={{ fontFamily: "var(--tenant-serif)" }}>
                        {p != null ? `from $${p}` : "—"}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
            <div className="text-center mt-10">
              <Link to={tenantHref("/booking")} data-testid="minimal-menu-cta"
                className="text-[10px] uppercase tracking-[0.42em] pb-1 transition-colors hover:opacity-70"
                style={{ borderBottom: "1px solid var(--tenant-accent, #C9A961)", color: "#0E0D0C" }}>
                Reserve a sitting →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── HOW IT WORKS — 3 quiet steps ─────────────────────────────────── */}
      <section className="pb-32 px-6" data-testid="minimal-home-process">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <Eyebrow>How it goes</Eyebrow>
            <Hairline className="mt-7" />
          </div>
          <ol className="grid grid-cols-1 sm:grid-cols-3 gap-12 sm:gap-10 text-center">
            {[
              { n: "I",   title: "Choose your sitting",   body: "Pick the package + tier that fits the moment you'd like to keep." },
              { n: "II",  title: "Reserve the morning",   body: "Hold the date with a small Zelle deposit. We'll write back to confirm." },
              { n: "III", title: "Step in front of the lens", body: "Arrive, breathe, and let us craft images you'll return to for years." },
            ].map((step) => (
              <li key={step.n} className="space-y-3">
                <div className="text-2xl italic font-light text-[color:var(--tenant-accent,#C9A961)]"
                     style={{ fontFamily: "var(--tenant-serif)" }}>{step.n}</div>
                <div className="text-xl font-light italic"
                     style={{ fontFamily: "var(--tenant-serif)" }}>{step.title}</div>
                <p className="text-sm text-[color:var(--tenant-fg,#0E0D0C)]/60 leading-relaxed max-w-[16rem] mx-auto"
                   style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── CLOSING WORD ─────────────────────────────────────────────────── */}
      <section className="pb-32 px-6 text-center" data-testid="minimal-home-close">
        <div className="max-w-xl mx-auto">
          <Eyebrow>When you're ready</Eyebrow>
          <Hairline className="mt-7 mb-8" />
          <h2 className="font-light italic leading-tight"
              style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.25rem, 4vw, 3.5rem)" }}>
            Let's keep one for you.
          </h2>
          <div className="mt-9">
            <Link to={tenantHref("/booking")} data-testid="minimal-close-cta"
              className="text-[11px] uppercase tracking-[0.42em] pb-1.5 transition-colors hover:opacity-80"
              style={{ borderBottom: "1px solid var(--tenant-accent, #C9A961)", color: "#0E0D0C" }}>
              Book a sitting →
            </Link>
          </div>
          {settings?.contact_email && (
            <a href={`mailto:${settings.contact_email}`}
               data-testid="minimal-home-contact"
               className="block mt-12 italic text-base sm:text-lg text-[color:var(--tenant-fg,#0E0D0C)]/55 hover:text-[color:var(--tenant-fg,#0E0D0C)] transition-colors"
               style={{ fontFamily: "var(--tenant-serif)" }}>
              {settings.contact_email}
            </a>
          )}
        </div>
      </section>
    </div>
  );
}
